import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, TextField, Typography } from '@mui/material';
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined';
import { FormDialog, StatusChip, SubmitButton, useToast } from '../../components';
import { ApiError } from '../../api/problemDetails';
import { restartForIdentityChange } from '../../api/whoami';
import { useStartImpersonation } from '../../api/impersonation';
import { SaUserPicker } from '../identity/components/SaUserPicker';
import type { SiteUserSearchResult } from '../identity/types';

/**
 * G1 — the console's GLOBAL cross-customer "View as user" launcher (opened from
 * {@link SaTopBar}). Searches every tenant (`GET /site/users/search`), picks an
 * active customer user, takes a **required, audited reason**, and starts a real
 * impersonation session (`POST /me/impersonation/start`). On success it
 * invalidates `['whoami']` and navigates to the portal root so the foundation
 * `AppShell` rebuilds as the target with the `ImpersonationBanner` (which owns
 * the exit). Internal DecisionPoint users and deactivated users are not valid
 * targets (the backend rejects them: no customer scope / inactive → 400); the
 * picker flags them inline and blocks submit. Mounted only while open.
 *
 * Distinct from the per-customer launcher on the customer detail page
 * (`siteadmin/customers/.../ImpersonationLauncherDialog`), which is scoped to one
 * customer's roster; this one searches across all customers.
 */
export function SaImpersonationLauncherDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const start = useStartImpersonation();

  const [selected, setSelected] = useState<SiteUserSearchResult | null>(null);
  const [reason, setReason] = useState('');

  // Reset the picked user + reason on each open/close transition (render-time
  // reset — matches GrantSiteAdminDialog).
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    setSelected(null);
    setReason('');
  }

  const invalidTarget = selected != null && (selected.isInternal || !selected.isActive);
  const canSubmit =
    selected != null && !invalidTarget && reason.trim().length > 0 && !start.isPending;

  const handleStart = async () => {
    if (!selected || invalidTarget || reason.trim().length === 0) return;
    try {
      await start.mutateAsync({ targetUserId: selected.id, reason: reason.trim() });
      // Restart rather than reset the cache and navigate (BUG-046). The dialog is
      // mounted by the always-present shell, so an in-SPA transition leaves the
      // admin's own tree on screen under the impersonated identity, and every query
      // it owns 403s. A reload is the only point at which the two never coexist.
      //
      // No success toast: the reload discards it before it can render, and the
      // impersonation banner that comes back says the same thing and keeps saying it.
      restartForIdentityChange();
    } catch (err) {
      const detail = err instanceof ApiError ? err.problem.detail : undefined;
      toast.error(detail ?? t('errors.generic'));
    }
  };

  const invalidChip = (u: SiteUserSearchResult) =>
    u.isInternal ? (
      <StatusChip severity="accent" label={t('siteAdmin:impersonate.internalChip')} />
    ) : !u.isActive ? (
      <StatusChip severity="error" label={t('siteAdmin:impersonate.inactiveChip')} />
    ) : null;

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={t('siteAdmin:impersonate.title')}
      footer={
        <>
          <Button variant="outlined" color="inherit" onClick={onClose}>
            {t('actions.cancel')}
          </Button>
          <SubmitButton
            type="button"
            startIcon={<VisibilityOutlined />}
            onClick={handleStart}
            disabled={!canSubmit}
            pending={start.isPending}
          >
            {t('siteAdmin:impersonate.start')}
          </SubmitButton>
        </>
      }
    >
      <Box>
        <Typography sx={{ fontSize: 12.5, fontWeight: 600, mb: 0.75 }}>
          {t('siteAdmin:impersonate.userLabel')}
          <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>
            *
          </Box>
        </Typography>
        <SaUserPicker
          value={selected}
          onPick={setSelected}
          onClear={() => setSelected(null)}
          // Internal DecisionPoint users have no customer scope and can therefore
          // never be an impersonation target — offering them is pure noise, so
          // they are filtered server-side (BUG-042).
          //
          // Deactivated users are deliberately NOT filtered: they are a
          // *meaningful* hit. Hiding them would answer "no users match" to a
          // search for someone who plainly exists, leaving the operator unable to
          // tell "wrong name" from "account is switched off". The inline chip and
          // the blocking alert below say which it is.
          filters={{ excludeInternal: true }}
          placeholder={t('siteAdmin:impersonate.userPlaceholder')}
          ariaLabel={t('siteAdmin:impersonate.userPlaceholder')}
          renderTrailing={invalidChip}
          selectedTrailing={invalidChip}
        />
        <Typography sx={{ mt: 0.75, fontSize: 12, color: 'text.secondary' }}>
          {t('siteAdmin:impersonate.userHint')}
        </Typography>
      </Box>

      {invalidTarget && (
        <Alert severity="error">
          {selected?.isInternal
            ? t('siteAdmin:impersonate.internalBlocked')
            : t('siteAdmin:impersonate.inactiveBlocked')}
        </Alert>
      )}

      <TextField
        size="small"
        fullWidth
        required
        multiline
        minRows={2}
        label={t('siteAdmin:impersonate.reasonLabel')}
        placeholder={t('siteAdmin:impersonate.reasonPlaceholder')}
        helperText={t('siteAdmin:impersonate.reasonHint')}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />

      {/* What-happens callout (amber, mirrors the prototype SaViewAsDialog). */}
      <Box
        sx={{
          display: 'flex',
          gap: 1.25,
          p: '11px 13px',
          borderRadius: '8px',
          bgcolor: '#FCF6E6',
          border: '1px solid rgba(224,161,6,0.4)',
        }}
      >
        <VisibilityOutlined sx={{ fontSize: 15, color: '#7A5200', mt: '1px', flexShrink: 0 }} />
        <Typography sx={{ fontSize: 12, lineHeight: 1.55, color: '#5A4310' }}>
          {t('siteAdmin:impersonate.callout')}
        </Typography>
      </Box>
    </FormDialog>
  );
}

export default SaImpersonationLauncherDialog;
